import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressWorkflowApiV1CardsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/_api/workflow-server-web',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/workflow-server-web',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/workflow-server-web',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/workflows',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_workflows_cards';

/**
 * Retrieves a list of a workflow's cards.
 *
 *
 * The `listCards()` function returns a Promise that resolves to a list of the specified workflow's cards.
 *
 *
 * Use the `options` parameter to specify which cards to retrieve and in which order to retrieve them. Must use either `phaseId` or `fetchOnlyArchived`.
 *
 *
 * Cards can be sorted based on their `"id"`, `"name"`, `"phaseId"`, `"createdDate"`, `"updatedDate"`, `"source"`, and `"position"`.
 *
 *
 * If no `limit` parameter is passed, the first 50 cards are returned.
 *
 *
 * Sort order defaults to by `"phaseId"` and then by `"position"` ascending.
 *
 *
 * This function requires you to specify the ID of a workflow. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 */
export function listCards(payload: object): RequestOptionsFactory<any> {
  function __listCards({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.card',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.CardsService.ListCards',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
        protoPath: '/v1/workflows/{workflowId}/cards',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'cards.info.dueDate' },
              { path: 'cards.info.createdAt' },
              { path: 'cards.info.updatedAt' },
              { path: 'cards.info.createdDate' },
              { path: 'cards.info.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
            protoPath: '/v1/workflows/{workflowId}/cards',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __listCards;
}

/**
 * Retrieves a workflow card by ID.
 *
 *
 * The `getCard()` function returns a Promise that resolves to the card with the specified ID.
 *
 *
 * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 */
export function getCard(payload: object): RequestOptionsFactory<any> {
  function __getCard({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.card',
      method: 'GET' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.CardsService.GetCard',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
        protoPath: '/v1/cards/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'card.info.dueDate' },
              { path: 'card.info.createdAt' },
              { path: 'card.info.updatedAt' },
              { path: 'card.info.createdDate' },
              { path: 'card.info.updatedDate' },
            ],
          },
        ]),
      fallback: [
        {
          method: 'GET' as any,
          url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
            protoPath: '/v1/cards/{id}',
            data: payload,
            host,
          }),
          params: toURLSearchParams(payload),
        },
      ],
    };

    return metadata;
  }

  return __getCard;
}

/**
 * Updates an existing workflow card.
 *
 *
 * The `updateCard()` function returns a Promise that resolves when the card has been updated with the specified values.
 *
 *
 * `contactId` is not a required field, but if the parameter is not passed, the field will default to empty and the card will not be associated with any contact.
 *
 *
 * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 */
export function updateCard(payload: object): RequestOptionsFactory<any> {
  function __updateCard({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'cardInfo.dueDate' },
          { path: 'cardInfo.createdAt' },
          { path: 'cardInfo.updatedAt' },
          { path: 'cardInfo.createdDate' },
          { path: 'cardInfo.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.workflows.v1.card',
      method: 'PATCH' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.CardsService.UpdateCard',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
        protoPath: '/v1/cards/{id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __updateCard;
}

/**
 * Creates a new workflow card.
 *
 *
 * The `createCard()` function returns a Promise that resolves to the created card's ID when the card is created in the specified workflow phase.
 *
 * Pass a value for the `position` parameter to specify the newly created card's position within the specified phase. Positions are zero-based, meaning the first position is `0`.
 *
 * When omitted, the newly created card is added to the specified phase as the top card within the phase.
 *
 *
 * This function requires you to specify the ID of a workflow and phase. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 *
 *  > **Note:**
 *  > Each workflow is limited to 5,000 cards.
 *  > If this limit is reached, `createCard()` throws an error.
 *  > [Archive cards](#archivecard) when they're no longer needed
 *  > to reduce card count and avoid hitting the limit.
 */
export function createCard(payload: object): RequestOptionsFactory<any> {
  function __createCard({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'card.dueDate' },
          { path: 'card.createdAt' },
          { path: 'card.updatedAt' },
          { path: 'card.createdDate' },
          { path: 'card.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.workflows.v1.card',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.CardsService.CreateCard',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
        protoPath: '/v1/workflows/{workflowId}/phases/{phaseId}/cards',
        data: serializedData,
        host,
      }),
      data: serializedData,
    };

    return metadata;
  }

  return __createCard;
}

/**
 * Deletes a workflow card by ID.
 *
 *
 * The `deleteCard()` function returns a Promise when the specified card has been deleted.
 *
 * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 */
export function deleteCard(payload: object): RequestOptionsFactory<any> {
  function __deleteCard({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.card',
      method: 'DELETE' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.CardsService.DeleteCard',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
        protoPath: '/v1/cards/{id}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCard;
}

/**
 * Moves a card to a new position within a workflow.
 *
 *
 * The `moveCard()` function returns a Promise when the specified card has been moved to the new position.
 *
 * Use the `options` parameter to specify the phase and position within that phase to move the card to. Positions are zero-based, meaning the first position is `0`.
 *
 * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 */
export function moveCard(payload: object): RequestOptionsFactory<any> {
  function __moveCard({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.card',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.CardsService.MoveCard',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
        protoPath: '/v1/cards/{id}/move',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __moveCard;
}

/**
 * Restores an archived workflow card.
 *
 *
 * The `restoreCard()` function returns a Promise that resolves when the archived card has been restored.
 *
 *
 * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 *
 *
 * > **Note:**
 * > Each workflow is limited to 5,000 cards.
 * > If this limit is reached, `createCard()` throws an error.
 * > [Archive cards](#archivecard) when they're no longer needed
 * > to reduce card count and avoid hitting the limit.
 */
export function restoreCard(payload: object): RequestOptionsFactory<any> {
  function __restoreCard({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.card',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.CardsService.RestoreCard',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
        protoPath: '/v1/cards/{id}/restore',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __restoreCard;
}

/**
 * Archives a workflow card.
 *
 *
 * The `archiveCard()` function returns a Promise that resolves when the card has been archived.
 *
 *
 * This function requires you to specify the ID of a card. To learn about retrieving IDs in the Workflows APIs, see [Retrieving IDs](wix-workflows-v2/introduction#retrieving-ids).
 */
export function archiveCard(payload: object): RequestOptionsFactory<any> {
  function __archiveCard({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.workflows.v1.card',
      method: 'POST' as any,
      methodFqn: 'com.wixpress.workflow.api.v1.CardsService.ArchiveCard',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressWorkflowApiV1CardsServiceUrl({
        protoPath: '/v1/cards/{id}/archive',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __archiveCard;
}
